/**
 * TopicPillarsSection — Phase B.3 pillar editor.
 *
 * Lists pillars from `projects/{pid}/topic_pillars/*` (filtered to the
 * default author when authorId is provided). Each pillar row shows name +
 * author-stance summary, with actions:
 *   • Edit (dialog form)
 *   • Confirm (clears isDraft flag)
 *   • Delete (with guard prompt)
 *
 * Shows a banner-level CTA "Confirm all drafts" when one or more pillars
 * are still drafts.
 */

import React, { useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Chip, Button, IconButton, TextField,
  Select, MenuItem, FormControl, InputLabel, Alert, Divider, Link as MuiLink,
  CircularProgress, Checkbox, FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import {
  SAccordion,
  BaseDialog,
  DialogHeader,
  DialogContent,
  DialogFooter,
  SDataTable,
  type SDataTableColumnDef,
  type SDataTableRowSelectionState,
} from '@modules/ui-components';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import { useBackendTask } from '@modules/content-agent/hooks/useBackendTask';
import { AvailableModelSelector } from '@modules/ai-settings';
import { useProjectDefaultModel } from '@modules/content-plan/hooks/useProjectDefaultModel';
import {
  ContentPlanApiClient,
  type PillarSourceReanalysisCurrentPillar,
  type PillarSourceReanalysisProposal,
  type PillarSourceReanalysisUpdateMode,
} from '@modules/content-plan/services/ContentPlanApiClient';
import { usePillars, type TopicPillarLite } from '../../hooks/usePillars';
import { TopicPillarService, type TopicPillarPatch } from '../../services/TopicPillarService';
import { useCollapsibleSection } from '../../hooks/useCollapsibleSection';
import { PillarCreateDialog } from './PillarCreateDialog';

interface Props {
  projectId: string;
  authorId?: string | null;
  userId: string;
  onTaskStarted?: (taskId: string, label: string) => void;
}

const TESTID = 'topic-pillars-section';

export const TopicPillarsSection: React.FC<Props> = ({
  projectId,
  authorId,
  userId,
  onTaskStarted,
}) => {
  const { pillars, loading } = usePillars(projectId);
  const service = useMemo(() => new TopicPillarService(), []);
  const apiClient = useMemo(() => new ContentPlanApiClient(), []);
  const [editingPillar, setEditingPillar] = useState<TopicPillarLite | null>(null);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [sourcePost, setSourcePost] = useState<SourcePostRef | null>(null);
  const [error, setError] = useState<string | null>(null);
  // DE1 — empty-state generation CTA state (CTA survives failure; AC-A042).
  const [generatingPillars, setGeneratingPillars] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const { expanded, setExpanded } = useCollapsibleSection('topic-pillars', true);

  const handleGenerateFromProfile = async () => {
    if (!authorId) {
      setGenerateError('No author selected — pillars are generated from an author profile.');
      return;
    }
    setGeneratingPillars(true);
    setGenerateError(null);
    try {
      const response = await apiClient.authorAnalyze({
        projectId,
        userId,
        existingAuthorId: authorId,
        scope: 'pillars',
      });
      if (!response.success || !response.taskId) {
        throw new Error(response.error ?? 'Pillar generation failed to start.');
      }
      onTaskStarted?.(response.taskId, 'Generate pillars');
    } catch (e: any) {
      setGenerateError(e?.message ?? 'Failed to start pillar generation');
    } finally {
      setGeneratingPillars(false);
    }
  };

  // Filter to current author's pillars when authorId provided. Otherwise
  // show all (admin-style view).
  const visible = useMemo(() => {
    if (!authorId) return pillars;
    return pillars.filter(
      (p) => Array.isArray(p.authorIds) && p.authorIds.includes(authorId),
    );
  }, [pillars, authorId]);

  const drafts = visible.filter((p) => p.isDraft);

  const handleConfirmAllDrafts = async () => {
    setError(null);
    try {
      await Promise.all(drafts.map((p) => service.confirm(projectId, p.id)));
    } catch (e: any) {
      setError(e?.message ?? 'Failed to confirm drafts');
    }
  };

  return (
    <SAccordion
      title="Topic pillars"
      subtitle={(() => {
        const parts: string[] = [
          'Author knowledge areas used for topic inspiration and post consistency.',
        ];
        if (visible.length > 0) parts.push(`${visible.length} pillar${visible.length === 1 ? '' : 's'}`);
        if (drafts.length > 0) parts.push(`${drafts.length} draft${drafts.length === 1 ? '' : 's'}`);
        return parts.join(' · ');
      })()}
      collapsible
      expanded={expanded}
      onChange={setExpanded}
      data-testid={TESTID}
    >
      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
          data-testid="topic-pillars-add-pillar"
        >
          Add pillar
        </Button>
        {drafts.length > 0 && (
          <Button
            variant="contained"
            size="small"
            startIcon={<CheckCircleIcon />}
            onClick={handleConfirmAllDrafts}
            data-testid="topic-pillars-confirm-all"
          >
            Confirm all drafts ({drafts.length})
          </Button>
        )}
      </Stack>

      <PillarCreateDialog
        open={createOpen}
        projectId={projectId}
        authorId={authorId ?? null}
        onClose={() => setCreateOpen(false)}
      />

      <PillarEditDialog
        open={Boolean(editingPillar)}
        pillar={editingPillar}
        projectId={projectId}
        authorId={authorId ?? null}
        userId={userId}
        onTaskStarted={onTaskStarted}
        onClose={() => setEditingPillar(null)}
        onSourcePostClick={setSourcePost}
        onSave={async (pillarId, patch) => {
          setError(null);
          try {
            await service.update(projectId, pillarId, patch);
            setEditingPillar(null);
          } catch (e: any) {
            setError(e?.message ?? 'Failed to save pillar');
          }
        }}
      />

      <SourcePostDialog
        open={Boolean(sourcePost)}
        projectId={projectId}
        authorId={authorId ?? null}
        source={sourcePost}
        onClose={() => setSourcePost(null)}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Typography variant="body2" color="text.secondary">Loading pillars…</Typography>
      ) : visible.length === 0 ? (
        <Stack spacing={1.5}>
          {/* DE1 — actionable empty state: perform the fixing action in
              place. Error keeps the CTA (retry), never bare text. */}
          {generateError && (
            <Alert severity="error" onClose={() => setGenerateError(null)}>
              {generateError}
            </Alert>
          )}
          <Alert
            severity="info"
            action={
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleGenerateFromProfile}
                  disabled={generatingPillars}
                  data-testid="pillars-empty-generate-cta"
                >
                  {generatingPillars ? 'Starting…' : 'Generate from profile'}
                </Button>
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => setCreateOpen(true)}
                  data-testid="pillars-empty-add-cta"
                >
                  Add manually
                </Button>
              </Stack>
            }
          >
            No pillars yet. Generate them from the author profile, or author
            one manually.
          </Alert>
        </Stack>
      ) : (
        <Stack divider={<Divider flexItem />} spacing={0}>
          {visible.map((pillar) => (
            <PillarRow
              key={pillar.id}
              pillar={pillar}
              isPendingDelete={pendingDelete === pillar.id}
              onEdit={() => setEditingPillar(pillar)}
              onSourcePostClick={setSourcePost}
              onConfirm={async () => {
                setError(null);
                try {
                  await service.confirm(projectId, pillar.id);
                } catch (e: any) {
                  setError(e?.message ?? 'Failed to confirm pillar');
                }
              }}
              onAskDelete={() => setPendingDelete(pillar.id)}
              onCancelDelete={() => setPendingDelete(null)}
              onConfirmDelete={async () => {
                setError(null);
                try {
                  await service.delete(projectId, pillar.id);
                  setPendingDelete(null);
                } catch (e: any) {
                  setError(e?.message ?? 'Failed to delete pillar');
                }
              }}
            />
          ))}
        </Stack>
      )}
    </SAccordion>
  );
};

interface RowProps {
  pillar: TopicPillarLite;
  isPendingDelete: boolean;
  onEdit: () => void;
  onSourcePostClick: (source: SourcePostRef) => void;
  onConfirm: () => Promise<void>;
  onAskDelete: () => void;
  onCancelDelete: () => void;
  onConfirmDelete: () => Promise<void>;
}

const PillarRow: React.FC<RowProps> = ({
  pillar, isPendingDelete,
  onEdit, onSourcePostClick,
  onConfirm, onAskDelete, onCancelDelete, onConfirmDelete,
}) => {
  const sourceRefs = collectSourcePostRefs(pillar);

  return (
    <Box sx={{ py: 2 }} data-testid={`pillar-row-${pillar.id}`}>
      <Stack direction="row" alignItems="flex-start" spacing={2}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap">
            <Typography variant="subtitle2">{pillar.name}</Typography>
            {pillar.isDraft && (
              <Chip
                label="draft"
                size="small"
                color="warning"
                data-testid={`pillar-${pillar.id}-draft-chip`}
              />
            )}
          </Stack>
          {(pillar.summary || pillar.description) && (
            <Typography
              variant="body2"
              sx={{ mt: 0.5 }}
            >
              {pillar.summary || pillar.description}
            </Typography>
          )}
          {pillar.description && pillar.summary && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {pillar.description}
            </Typography>
          )}
          {pillar.authorStance && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              {pillar.authorStance}
            </Typography>
          )}
          {pillar.knowledgeContext && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1, whiteSpace: 'pre-wrap' }}
            >
              {pillar.knowledgeContext.length > 700
                ? `${pillar.knowledgeContext.slice(0, 700)}…`
                : pillar.knowledgeContext}
            </Typography>
          )}
          {(pillar.keyTheses?.length || sourceRefs.length || pillar.guardrails?.length) && (
            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
              {pillar.keyTheses?.length ? (
                <Chip label={`${pillar.keyTheses.length} theses`} size="small" variant="outlined" />
              ) : null}
              {sourceRefs.length ? (
                <Chip label={`${sourceRefs.length} sources`} size="small" variant="outlined" />
              ) : null}
              {pillar.guardrails?.length ? (
                <Chip label={`${pillar.guardrails.length} guardrails`} size="small" variant="outlined" color="warning" />
              ) : null}
            </Stack>
          )}
          {pillar.keyTheses?.length ? (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">Key theses</Typography>
              {pillar.keyTheses.slice(0, 4).map((item, index) => (
                <Typography key={`${pillar.id}-thesis-${index}`} variant="body2" sx={{ mt: 0.25 }}>
                  {item.thesis}
                </Typography>
              ))}
            </Box>
          ) : null}
          {sourceRefs.length ? (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">Source posts</Typography>
              <SourcePostLinks
                sources={sourceRefs}
                onClick={onSourcePostClick}
              />
            </Box>
          ) : null}
          {pillar.sourceHighlights?.length ? (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">Source highlights</Typography>
              {pillar.sourceHighlights.slice(0, 3).map((item, index) => (
                <Typography key={`${pillar.id}-highlight-${index}`} variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  <SourcePostLink
                    source={{ postId: item.postId, platformId: item.platformId }}
                    onClick={onSourcePostClick}
                  />
                  {': '}
                  {item.quoteOrParaphrase}
                </Typography>
              ))}
            </Box>
          ) : null}
          {pillar.guardrails?.length ? (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">Guardrails</Typography>
              {pillar.guardrails.slice(0, 4).map((item, index) => (
                <Typography key={`${pillar.id}-guardrail-${index}`} variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {item}
                </Typography>
              ))}
            </Box>
          ) : null}
        </Box>
        <Stack direction="row" spacing={0.5}>
          {pillar.isDraft && (
            <Button
              size="small"
              variant="text"
              startIcon={<CheckCircleIcon fontSize="small" />}
              onClick={onConfirm}
              data-testid={`pillar-${pillar.id}-confirm-btn`}
            >
              Confirm
            </Button>
          )}
          <IconButton
            size="small"
            onClick={onEdit}
            data-testid={`pillar-${pillar.id}-edit-btn`}
            aria-label="Edit pillar"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={onAskDelete}
            data-testid={`pillar-${pillar.id}-delete-btn`}
            aria-label="Delete pillar"
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
      {isPendingDelete && (
        <Alert
          severity="warning"
          sx={{ mt: 1.5 }}
          action={
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={onCancelDelete}>Cancel</Button>
              <Button
                size="small"
                color="error"
                variant="contained"
                onClick={onConfirmDelete}
                data-testid={`pillar-${pillar.id}-confirm-delete-btn`}
              >
                Delete
              </Button>
            </Stack>
          }
        >
          Delete this pillar? Existing topics keep their stored pillarIds, but
          future generation will no longer use it.
        </Alert>
      )}
    </Box>
  );
};

interface PillarEditDialogProps {
  open: boolean;
  pillar: TopicPillarLite | null;
  projectId: string;
  authorId: string | null;
  userId: string;
  onTaskStarted?: (taskId: string, label: string) => void;
  onClose: () => void;
  onSave: (pillarId: string, patch: TopicPillarPatch) => Promise<void>;
  onSourcePostClick: (source: SourcePostRef) => void;
}

const PillarEditDialog: React.FC<PillarEditDialogProps> = ({
  open,
  pillar,
  projectId,
  authorId,
  userId,
  onTaskStarted,
  onClose,
  onSave,
  onSourcePostClick,
}) => {
  const apiClient = useMemo(() => new ContentPlanApiClient(), []);
  const [name, setName] = useState(pillar?.name ?? '');
  const [summary, setSummary] = useState(pillar?.summary ?? '');
  const [description, setDescription] = useState(pillar?.description ?? '');
  const [stance, setStance] = useState(pillar?.authorStance ?? '');
  const [knowledgeContext, setKnowledgeContext] = useState(pillar?.knowledgeContext ?? '');
  const [keyThesesText, setKeyThesesText] = useState((pillar?.keyTheses ?? []).map((t) => t.thesis).join('\n'));
  const [keyThesesDraft, setKeyThesesDraft] = useState<TopicPillarLite['keyTheses']>(pillar?.keyTheses ?? []);
  const [sourceRefs, setSourceRefs] = useState<SourcePostRef[]>([]);
  const [sourceHighlightsDraft, setSourceHighlightsDraft] = useState<TopicPillarLite['sourceHighlights']>(pillar?.sourceHighlights ?? []);
  const [postPickerOpen, setPostPickerOpen] = useState(false);
  const [guardrailsText, setGuardrailsText] = useState((pillar?.guardrails ?? []).join('\n'));
  const [saving, setSaving] = useState(false);
  const [reanalysisTaskId, setReanalysisTaskId] = useState<string | null>(null);
  const [reanalysisError, setReanalysisError] = useState<string | null>(null);
  const [reanalysisProposal, setReanalysisProposal] = useState<PillarSourceReanalysisProposal | null>(null);
  const [proposalOpen, setProposalOpen] = useState(false);
  const { task: reanalysisTask } = useBackendTask(
    reanalysisTaskId ? projectId : null,
    reanalysisTaskId,
  );

  React.useEffect(() => {
    if (!open || !pillar) return;
    setName(pillar.name ?? '');
    setSummary(pillar.summary ?? '');
    setDescription(pillar.description ?? '');
    setStance(pillar.authorStance ?? '');
    setKnowledgeContext(pillar.knowledgeContext ?? '');
    setKeyThesesText((pillar.keyTheses ?? []).map((t) => t.thesis).join('\n'));
    setKeyThesesDraft(pillar.keyTheses ?? []);
    setSourceRefs(collectSourcePostRefs(pillar));
    setSourceHighlightsDraft(pillar.sourceHighlights ?? []);
    setGuardrailsText((pillar.guardrails ?? []).join('\n'));
    setReanalysisTaskId(null);
    setReanalysisError(null);
    setReanalysisProposal(null);
    setProposalOpen(false);
  }, [open, pillar]);

  React.useEffect(() => {
    if (!reanalysisTaskId || !reanalysisTask) return;
    if (reanalysisTask.status === 'completed') {
      const proposal = (reanalysisTask.result as { proposal?: PillarSourceReanalysisProposal } | undefined)?.proposal;
      if (proposal) {
        setReanalysisProposal(proposal);
        setProposalOpen(true);
      } else {
        setReanalysisError('Pillar reanalysis completed without a proposal.');
      }
      setReanalysisTaskId(null);
    }
    if (reanalysisTask.status === 'error') {
      setReanalysisError(reanalysisTask.error ?? 'Pillar reanalysis failed.');
      setReanalysisTaskId(null);
    }
  }, [reanalysisTask, reanalysisTaskId]);

  const splitLines = (value: string) =>
    value.split('\n').map((line) => line.trim()).filter(Boolean);

  if (!pillar) return null;

  const sourceIds = new Set(sourceRefs.map((source) => source.postId));
  const buildKeyThesesDraft = () =>
    splitLines(keyThesesText).map((thesis, index) => ({
      thesis,
      confidence: keyThesesDraft?.[index]?.confidence,
      sourcePostIds: (keyThesesDraft?.[index]?.sourcePostIds ?? []).filter(
        (postId) => sourceIds.has(postId),
      ),
    }));

  const buildCurrentPillarDraft = (
    refs: SourcePostRef[] = sourceRefs,
  ): PillarSourceReanalysisCurrentPillar => {
    const nextSourceIds = new Set(refs.map((source) => source.postId));
    return {
      id: pillar.id,
      name: name.trim() || pillar.name,
      summary: summary.trim() || undefined,
      description: description.trim() || undefined,
      authorStance: stance.trim() || undefined,
      knowledgeContext: knowledgeContext.trim() || undefined,
      keyTheses: splitLines(keyThesesText).map((thesis, index) => ({
        thesis,
        confidence: keyThesesDraft?.[index]?.confidence,
        sourcePostIds: (keyThesesDraft?.[index]?.sourcePostIds ?? []).filter(
          (postId) => nextSourceIds.has(postId),
        ),
      })),
      sourcePostIds: refs.map((source) => source.postId),
      sourceHighlights: buildSourceHighlights(
        { ...pillar, sourceHighlights: sourceHighlightsDraft } as TopicPillarLite,
        refs,
      ),
      guardrails: splitLines(guardrailsText),
      funnelStage: pillar.funnelStage,
      weight: pillar.weight,
    };
  };

  const handleAddSources = async (
    nextSources: SourcePostRef[],
    options: {
      updateAnalysis: boolean;
      model?: string;
      updateMode: PillarSourceReanalysisUpdateMode;
    },
  ) => {
    const currentIds = new Set(sourceRefs.map((source) => source.postId));
    const newlyAdded = nextSources.filter((source) => !currentIds.has(source.postId));
    const mergedRefs = mergeSourceRefs(sourceRefs, nextSources);
    setSourceRefs(mergedRefs);
    setPostPickerOpen(false);

    if (!options.updateAnalysis || newlyAdded.length === 0) return;
    setReanalysisError(null);
    setReanalysisProposal(null);

    if (!authorId) {
      setReanalysisError('No author selected for pillar reanalysis.');
      return;
    }

    try {
      const response = await apiClient.reanalyzePillarSources({
        projectId,
        userId,
        authorId,
        pillarId: pillar.id,
        currentPillar: buildCurrentPillarDraft(mergedRefs),
        newSourcePosts: newlyAdded,
        updatePolicy: {
          mode: options.updateMode,
          existingSourcePostCount: currentIds.size,
          newlyAddedPostCount: newlyAdded.length,
          maxNewTheses: options.updateMode === 'expansive'
            ? 5
            : options.updateMode === 'balanced'
              ? 4
              : 3,
        },
        model: options.model,
      });
      if (!response.taskId) {
        throw new Error(response.error ?? 'Pillar reanalysis did not return a task id.');
      }
      setReanalysisTaskId(response.taskId);
      onTaskStarted?.(response.taskId, 'Update pillar analysis');
    } catch (e: any) {
      setReanalysisError(e?.message ?? 'Failed to start pillar reanalysis.');
    }
  };

  const handleRemoveSource = (postId: string) => {
    setSourceRefs((current) => current.filter((source) => source.postId !== postId));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(pillar.id, {
        name: name.trim() || pillar.name,
        summary: summary.trim() || undefined,
        description: description.trim() || undefined,
        authorStance: stance.trim() || undefined,
        knowledgeContext: knowledgeContext.trim() || undefined,
        keyTheses: buildKeyThesesDraft(),
        sourcePostIds: sourceRefs.map((source) => source.postId),
        sourceHighlights: buildSourceHighlights(
          { ...pillar, sourceHighlights: sourceHighlightsDraft } as TopicPillarLite,
          sourceRefs,
        ),
        guardrails: splitLines(guardrailsText),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleApplyProposal = (fields: ProposalFieldKey[]) => {
    if (!reanalysisProposal) return;
    const selected = new Set(fields);
    if (selected.has('summary')) setSummary(reanalysisProposal.summary ?? '');
    if (selected.has('description')) setDescription(reanalysisProposal.description ?? '');
    if (selected.has('knowledgeContext')) setKnowledgeContext(reanalysisProposal.knowledgeContext ?? '');
    if (selected.has('keyTheses')) {
      const next = reanalysisProposal.keyTheses ?? [];
      setKeyThesesDraft(next);
      setKeyThesesText(next.map((item) => item.thesis).join('\n'));
    }
    if (selected.has('sourceHighlights')) {
      setSourceHighlightsDraft(reanalysisProposal.sourceHighlights ?? []);
    }
    if (selected.has('guardrails')) setGuardrailsText((reanalysisProposal.guardrails ?? []).join('\n'));
    if (selected.has('authorStance')) setStance(reanalysisProposal.authorStance ?? '');
    setProposalOpen(false);
  };

  return (
    <BaseDialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      data-testid={`pillar-${pillar.id}-edit-dialog`}
    >
      <PostSourcePickerDialog
        open={postPickerOpen}
        projectId={projectId}
        authorId={authorId}
        selectedPostIds={sourceRefs.map((source) => source.postId)}
        onClose={() => setPostPickerOpen(false)}
        onAdd={handleAddSources}
      />
      <PillarReanalysisProposalDialog
        open={proposalOpen}
        proposal={reanalysisProposal}
        current={buildCurrentPillarDraft()}
        onClose={() => setProposalOpen(false)}
        onApply={handleApplyProposal}
      />
      <DialogHeader
        title="Edit pillar"
        subtitle={pillar.name}
        onClose={saving ? undefined : onClose}
      />
      <DialogContent sx={{ maxHeight: '75vh', overflow: 'auto' }}>
        <Stack spacing={1.5}>
          {reanalysisTaskId && (
            <Alert
              severity="info"
              icon={<CircularProgress size={16} />}
              data-testid={`pillar-${pillar.id}-reanalysis-running`}
            >
              Updating analysis from selected source posts…
            </Alert>
          )}
          {reanalysisError && (
            <Alert severity="error" onClose={() => setReanalysisError(null)}>
              {reanalysisError}
            </Alert>
          )}
          <TextField
            label="Name"
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            inputProps={{ 'data-testid': `pillar-${pillar.id}-name-input` }}
            fullWidth
          />
          <TextField
            label="Summary"
            size="small"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            inputProps={{ 'data-testid': `pillar-${pillar.id}-summary-input` }}
            fullWidth
          />
          <TextField
            label="Description"
            size="small"
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            inputProps={{ 'data-testid': `pillar-${pillar.id}-description-input` }}
            fullWidth
          />
          <TextField
            label="Knowledge context"
            size="small"
            multiline
            minRows={4}
            value={knowledgeContext}
            onChange={(e) => setKnowledgeContext(e.target.value)}
            inputProps={{ 'data-testid': `pillar-${pillar.id}-knowledge-context-input` }}
            fullWidth
          />
          <TextField
            label="Key theses"
            size="small"
            multiline
            minRows={3}
            value={keyThesesText}
            onChange={(e) => setKeyThesesText(e.target.value)}
            inputProps={{ 'data-testid': `pillar-${pillar.id}-key-theses-input` }}
            helperText="One thesis per line."
            fullWidth
          />
          <Box>
            <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
              <Box>
                <Typography variant="subtitle2">Source posts</Typography>
                <Typography variant="caption" color="text.secondary">
                  Select posts from the author corpus.
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setPostPickerOpen(true)}
                data-testid={`pillar-${pillar.id}-add-source-posts-btn`}
              >
                Add posts
              </Button>
            </Stack>
            {sourceRefs.length > 0 ? (
              <SourcePostLinks
                sources={sourceRefs}
                onClick={onSourcePostClick}
                onDelete={handleRemoveSource}
              />
            ) : (
              <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
                No source posts selected.
              </Alert>
            )}
          </Box>
          <TextField
            label="Guardrails"
            size="small"
            multiline
            minRows={2}
            value={guardrailsText}
            onChange={(e) => setGuardrailsText(e.target.value)}
            inputProps={{ 'data-testid': `pillar-${pillar.id}-guardrails-input` }}
            helperText="One contradiction rule per line."
            fullWidth
          />
          <TextField
            label="Author stance"
            size="small"
            multiline
            minRows={2}
            value={stance}
            onChange={(e) => setStance(e.target.value)}
            inputProps={{ 'data-testid': `pillar-${pillar.id}-stance-input` }}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogFooter
        secondaryAction={{
          label: 'Cancel',
          onClick: onClose,
          disabled: saving,
          startIcon: <CloseIcon />,
        }}
        primaryAction={{
          label: saving ? 'Saving…' : 'Save',
          onClick: handleSave,
          disabled: saving,
          loading: saving,
          startIcon: <SaveIcon />,
          'data-testid': `pillar-${pillar.id}-save-btn`,
        }}
      />
    </BaseDialog>
  );
};

interface SourcePostRef {
  postId: string;
  platformId?: string;
  preview?: string;
}

interface SourcePostLinksProps {
  sources: SourcePostRef[];
  onClick: (source: SourcePostRef) => void;
  onDelete?: (postId: string) => void;
}

const SourcePostLinks: React.FC<SourcePostLinksProps> = ({ sources, onClick, onDelete }) => (
  <Stack direction="row" spacing={0.75} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
    {sources.map((source) => (
      <Chip
        key={`${source.platformId ?? 'any'}-${source.postId}`}
        label={source.preview ? truncate(source.preview, 72) : source.postId}
        title={source.postId}
        size="small"
        variant="outlined"
        clickable
        onClick={() => onClick(source)}
        onDelete={onDelete ? () => onDelete(source.postId) : undefined}
        data-testid={`pillar-source-post-${source.postId}`}
      />
    ))}
  </Stack>
);

const SourcePostLink: React.FC<{
  source: SourcePostRef;
  onClick: (source: SourcePostRef) => void;
}> = ({ source, onClick }) => (
  <MuiLink
    component="button"
    type="button"
    variant="body2"
    onClick={() => onClick(source)}
    sx={{ verticalAlign: 'baseline' }}
  >
    {source.postId}
  </MuiLink>
);

type ProposalFieldKey =
  | 'summary'
  | 'description'
  | 'knowledgeContext'
  | 'keyTheses'
  | 'sourceHighlights'
  | 'guardrails'
  | 'authorStance';

const PROPOSAL_FIELDS: Array<{ key: ProposalFieldKey; label: string }> = [
  { key: 'summary', label: 'Summary' },
  { key: 'description', label: 'Description' },
  { key: 'knowledgeContext', label: 'Knowledge context' },
  { key: 'keyTheses', label: 'Key theses' },
  { key: 'sourceHighlights', label: 'Source highlights' },
  { key: 'guardrails', label: 'Guardrails' },
  { key: 'authorStance', label: 'Author stance' },
];

const DEFAULT_SELECTED_PROPOSAL_FIELDS = new Set<ProposalFieldKey>([
  'summary',
  'keyTheses',
  'sourceHighlights',
]);

function makeDefaultProposalSelection(): Record<ProposalFieldKey, boolean> {
  return Object.fromEntries(
    PROPOSAL_FIELDS.map((field) => [
      field.key,
      DEFAULT_SELECTED_PROPOSAL_FIELDS.has(field.key),
    ]),
  ) as Record<ProposalFieldKey, boolean>;
}

const PillarReanalysisProposalDialog: React.FC<{
  open: boolean;
  proposal: PillarSourceReanalysisProposal | null;
  current: PillarSourceReanalysisCurrentPillar;
  onClose: () => void;
  onApply: (fields: ProposalFieldKey[]) => void;
}> = ({ open, proposal, current, onClose, onApply }) => {
  const [selected, setSelected] = useState<Record<ProposalFieldKey, boolean>>(
    makeDefaultProposalSelection,
  );

  React.useEffect(() => {
    if (!open) return;
    setSelected(makeDefaultProposalSelection());
  }, [open, proposal]);

  if (!proposal) return null;

  const selectedFields = PROPOSAL_FIELDS
    .filter((field) => selected[field.key])
    .map((field) => field.key);

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      data-testid="pillar-reanalysis-proposal-dialog"
    >
      <DialogHeader
        title="Review analysis proposal"
        subtitle={proposal.hasMaterialChanges === false ? 'No material changes detected' : 'Select fields to apply'}
        onClose={onClose}
      />
      <DialogContent sx={{ maxHeight: '75vh', overflow: 'auto' }}>
        <Stack spacing={1.5}>
          <Alert severity={proposal.hasMaterialChanges === false ? 'info' : 'success'}>
            {proposal.changeSummary}
          </Alert>
          <Typography variant="caption" color="text.secondary">
            Broad fields are unchecked by default; apply them only when the new posts materially change the pillar.
          </Typography>
          {PROPOSAL_FIELDS.map((field) => (
            <Box key={field.key} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selected[field.key]}
                    onChange={(event) =>
                      setSelected((currentSelected) => ({
                        ...currentSelected,
                        [field.key]: event.target.checked,
                      }))
                    }
                    size="small"
                  />
                }
                label={field.label}
              />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary">Current</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {formatProposalField(current, field.key) || '—'}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary">Proposed</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {formatProposalField(proposal, field.key) || '—'}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogFooter
        secondaryAction={{
          label: 'Dismiss',
          onClick: onClose,
        }}
        primaryAction={{
          label: 'Apply selected',
          onClick: () => onApply(selectedFields),
          disabled: selectedFields.length === 0,
          startIcon: <CheckCircleIcon />,
          'data-testid': 'pillar-reanalysis-apply-selected',
        }}
      />
    </BaseDialog>
  );
};

function formatProposalField(
  source: PillarSourceReanalysisCurrentPillar | PillarSourceReanalysisProposal,
  key: ProposalFieldKey,
): string {
  const value = source[key];
  if (typeof value === 'string') return value;
  if (!Array.isArray(value) || value.length === 0) return '';
  if (key === 'keyTheses') {
    return value
      .map((item: any) => {
        const sourceIds = Array.isArray(item.sourcePostIds) && item.sourcePostIds.length > 0
          ? ` [${item.sourcePostIds.join(', ')}]`
          : '';
        const confidence = typeof item.confidence === 'number' ? ` (${item.confidence.toFixed(2)})` : '';
        return `${item.thesis}${confidence}${sourceIds}`;
      })
      .join('\n');
  }
  if (key === 'sourceHighlights') {
    return value
      .map((item: any) => `${item.postId}: ${item.quoteOrParaphrase} — ${item.relevance}`)
      .join('\n');
  }
  return value.join('\n');
}

function mergeSourceRefs(current: SourcePostRef[], next: SourcePostRef[]): SourcePostRef[] {
  const byId = new Map(current.map((source) => [source.postId, source]));
  next.forEach((source) => {
    byId.set(source.postId, {
      ...byId.get(source.postId),
      ...source,
    });
  });
  return Array.from(byId.values());
}

function collectSourcePostRefs(pillar: TopicPillarLite): SourcePostRef[] {
  const byId = new Map<string, SourcePostRef>();
  const add = (postId?: string, platformId?: string, preview?: string) => {
    if (!postId) return;
    const current = byId.get(postId);
    byId.set(postId, {
      postId,
      platformId: current?.platformId ?? platformId,
      preview: current?.preview ?? preview,
    });
  };

  pillar.sourcePostIds?.forEach((postId) => add(postId));
  pillar.sourceHighlights?.forEach((item) =>
    add(item.postId, item.platformId, item.quoteOrParaphrase),
  );
  pillar.keyTheses?.forEach((thesis) => {
    thesis.sourcePostIds?.forEach((postId) => add(postId));
  });

  return Array.from(byId.values());
}

function buildSourceHighlights(
  pillar: TopicPillarLite,
  sourceRefs: SourcePostRef[],
): TopicPillarPatch['sourceHighlights'] {
  const existing = new Map(
    (pillar.sourceHighlights ?? []).map((highlight) => [highlight.postId, highlight]),
  );
  return sourceRefs.map((source) => {
    const highlight = existing.get(source.postId);
    if (highlight) {
      return {
        ...highlight,
        platformId: highlight.platformId ?? source.platformId,
      };
    }
    return {
      postId: source.postId,
      platformId: source.platformId,
      quoteOrParaphrase: source.preview
        ? truncate(source.preview, 240)
        : `Source post ${source.postId}`,
      relevance: 'Manually selected as supporting source for this pillar.',
    };
  });
}

interface PostSourcePickerDialogProps {
  open: boolean;
  projectId: string;
  authorId: string | null;
  selectedPostIds: string[];
  onClose: () => void;
  onAdd: (
    sources: SourcePostRef[],
    options: {
      updateAnalysis: boolean;
      model?: string;
      updateMode: PillarSourceReanalysisUpdateMode;
    },
  ) => void | Promise<void>;
}

interface SourcePostOption extends SourcePostRecord {
  preview: string;
  source?: string;
  engagement?: {
    likes?: number;
    comments?: number;
    shares?: number;
    views?: number;
  };
}

const PostSourcePickerDialog: React.FC<PostSourcePickerDialogProps> = ({
  open,
  projectId,
  authorId,
  selectedPostIds,
  onClose,
  onAdd,
}) => {
  const projectDefaultModel = useProjectDefaultModel(projectId);
  const [posts, setPosts] = useState<SourcePostOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rowSelection, setRowSelection] = useState<SDataTableRowSelectionState>({});
  const [updateAnalysis, setUpdateAnalysis] = useState(false);
  const [reanalysisModel, setReanalysisModel] = useState(projectDefaultModel.value);
  const [updateMode, setUpdateMode] = useState<PillarSourceReanalysisUpdateMode>('conservative');
  const selectedSet = useMemo(() => new Set(selectedPostIds), [selectedPostIds]);

  React.useEffect(() => {
    let cancelled = false;
    if (!open) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      setRowSelection({});
      setUpdateAnalysis(false);
      setUpdateMode('conservative');
      try {
        if (!authorId) {
          throw new Error('No author selected for this pillar.');
        }
        const loaded = await loadAuthorPosts(projectId, authorId);
        if (!cancelled) setPosts(loaded);
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? 'Failed to load author posts');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, projectId, authorId]);

  React.useEffect(() => {
    if (!open) return;
    setReanalysisModel(projectDefaultModel.value);
  }, [open, projectDefaultModel.value]);

  const columns = useMemo<SDataTableColumnDef<SourcePostOption>[]>(
    () => [
      {
        accessorKey: 'preview',
        header: 'Post / preview',
        Cell: ({ row }) => (
          <Typography variant="body2" sx={{ maxWidth: 520 }}>
            {truncate(row.original.preview, 140)}
          </Typography>
        ),
        size: 520,
      },
      {
        accessorKey: 'publishedAt',
        header: 'Published',
        Cell: ({ cell }) => (
          <Typography variant="body2" color="text.secondary">
            {formatPostDate(cell.getValue()) ?? '—'}
          </Typography>
        ),
        size: 130,
        sortingFn: (a, b) =>
          getComparableDate(a.original.publishedAt) - getComparableDate(b.original.publishedAt),
      },
      {
        accessorKey: 'engagement.likes',
        header: 'Likes',
        Cell: ({ row }) => (
          <Typography variant="body2">{row.original.engagement?.likes ?? '—'}</Typography>
        ),
        size: 80,
      },
      {
        accessorKey: 'platformId',
        header: 'Platform',
        Cell: ({ cell }) => <Chip label={cell.getValue<string>()} size="small" variant="outlined" />,
        size: 110,
      },
      {
        accessorKey: 'source',
        header: 'Source',
        Cell: ({ cell }) => {
          const value = cell.getValue<string>();
          return value ? <Chip label={value} size="small" variant="outlined" /> : null;
        },
        size: 110,
      },
    ],
    [],
  );

  const handleAdd = () => {
    const picked = posts
      .filter((post) => rowSelection[post.id] && !selectedSet.has(post.id))
      .map((post) => ({
        postId: post.id,
        platformId: post.platformId,
        preview: post.preview,
      }));
    onAdd(picked, {
      updateAnalysis,
      model: updateAnalysis ? reanalysisModel : undefined,
      updateMode,
    });
  };

  const pickedCount = Object.values(rowSelection).filter(Boolean).length;

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      data-testid="pillar-post-source-picker-dialog"
    >
      <DialogHeader
        title="Add source posts"
        subtitle="Choose supporting posts from this author's corpus."
        onClose={onClose}
      />
      <DialogContent sx={{ minHeight: 520 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={updateAnalysis}
              onChange={(event) => setUpdateAnalysis(event.target.checked)}
              size="small"
              data-testid="pillar-post-source-picker-update-analysis"
            />
          }
          label="Update analysis after adding"
          sx={{ mb: 1 }}
        />
        {updateAnalysis && (
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', md: 'center' }}
            sx={{ mb: 1.5 }}
          >
            <AvailableModelSelector
              projectId={projectId}
              value={reanalysisModel}
              onChange={setReanalysisModel}
              label="Reanalysis model"
              size="small"
              sx={{ minWidth: 260, flex: 1 }}
              testId="pillar-post-source-picker-reanalysis-model"
            />
            <FormControl size="small" sx={{ minWidth: 240, flex: 1 }}>
              <InputLabel>Update strength</InputLabel>
              <Select
                value={updateMode}
                label="Update strength"
                onChange={(event) =>
                  setUpdateMode(event.target.value as PillarSourceReanalysisUpdateMode)
                }
                data-testid="pillar-post-source-picker-update-mode"
              >
                <MenuItem value="conservative">Conservative: theses only</MenuItem>
                <MenuItem value="balanced">Balanced: light basics</MenuItem>
                <MenuItem value="expansive">Expansive: broader proposal</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        )}
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : loading ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">Loading posts…</Typography>
          </Stack>
        ) : (
          <SDataTable<SourcePostOption>
            columns={columns}
            data={posts}
            getRowId={(row) => row.id}
            enableRowSelection={(row) => !selectedSet.has(row.original.id)}
            enableSorting
            enablePagination
            enableBottomToolbar
            enableSearch
            searchInColumn="preview"
            searchMode="global"
            searchPlaceholder="Search posts..."
            state={{ rowSelection }}
            onRowSelectionChange={setRowSelection}
            initialState={{
              sorting: [{ id: 'publishedAt', desc: true }],
              pagination: { pageIndex: 0, pageSize: 10 },
            }}
            muiTableContainerProps={{ sx: { maxHeight: 420 } }}
            muiTableBodyRowProps={({ row }) => ({
              hover: true,
              sx: {
                cursor: selectedSet.has(row.original.id) ? 'default' : 'pointer',
                opacity: selectedSet.has(row.original.id) ? 0.55 : 1,
              },
              onClick: () => {
                if (selectedSet.has(row.original.id)) return;
                setRowSelection((current) => ({
                  ...current,
                  [row.original.id]: !current[row.original.id],
                }));
              },
            })}
            renderEmptyRowsFallback={() => (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No posts found in this author's corpus.
                </Typography>
              </Box>
            )}
          />
        )}
      </DialogContent>
      <DialogFooter
        secondaryAction={{
          label: 'Cancel',
          onClick: onClose,
        }}
        primaryAction={{
          label: pickedCount > 0 ? `Add ${pickedCount} post${pickedCount === 1 ? '' : 's'}` : 'Add posts',
          onClick: handleAdd,
          disabled: pickedCount === 0,
          startIcon: <AddIcon />,
          'data-testid': 'pillar-post-source-picker-add-btn',
        }}
      />
    </BaseDialog>
  );
};

interface SourcePostDialogProps {
  open: boolean;
  projectId: string;
  authorId: string | null;
  source: SourcePostRef | null;
  onClose: () => void;
}

interface SourcePostRecord {
  id: string;
  platformId: string;
  hook?: string;
  body?: string;
  url?: string;
  sourceUrl?: string;
  publishedAt?: unknown;
  rawData?: Record<string, unknown>;
}

const SourcePostDialog: React.FC<SourcePostDialogProps> = ({
  open,
  projectId,
  authorId,
  source,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [post, setPost] = useState<SourcePostRecord | null>(null);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    if (!open || !source) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      setPost(null);
      try {
        if (!authorId) {
          throw new Error('No author selected for this pillar.');
        }
        const found = await loadSourcePost(projectId, authorId, source);
        if (!found) {
          throw new Error(`Post ${source.postId} was not found in this author's corpus.`);
        }
        if (!cancelled) setPost(found);
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? 'Failed to load source post');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, projectId, authorId, source]);

  const externalUrl = post?.url ?? post?.sourceUrl;

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid="pillar-source-post-dialog"
    >
      <DialogHeader
        title="Source post"
        subtitle={source?.postId}
        onClose={onClose}
      />
      <DialogContent>
        {loading ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">Loading post…</Typography>
          </Stack>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : post ? (
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={post.platformId} size="small" variant="outlined" />
              <Chip label={post.id} size="small" variant="outlined" />
              {formatPostDate(post.publishedAt) && (
                <Chip label={formatPostDate(post.publishedAt)} size="small" variant="outlined" />
              )}
            </Stack>
            {post.hook && (
              <Box>
                <Typography variant="caption" color="text.secondary">Hook</Typography>
                <Typography variant="body2">{post.hook}</Typography>
              </Box>
            )}
            {post.body && (
              <Box>
                <Typography variant="caption" color="text.secondary">Body</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {post.body}
                </Typography>
              </Box>
            )}
            {externalUrl && (
              <Button
                href={externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                size="small"
                startIcon={<OpenInNewIcon fontSize="small" />}
                sx={{ alignSelf: 'flex-start' }}
              >
                Open original post
              </Button>
            )}
          </Stack>
        ) : null}
      </DialogContent>
      <DialogFooter
        primaryAction={{
          label: 'Close',
          onClick: onClose,
          variant: 'outlined',
        }}
      />
    </BaseDialog>
  );
};

async function loadSourcePost(
  projectId: string,
  authorId: string,
  source: SourcePostRef,
): Promise<SourcePostRecord | null> {
  const db = CoreFirebaseService.getInstance().firestore;
  const platformIds = source.platformId
    ? [source.platformId]
    : (await getDocs(collection(db, 'projects', projectId, 'authors', authorId, 'platforms')))
      .docs
      .map((platformDoc) => platformDoc.id);

  for (const platformId of platformIds) {
    const postSnap = await getDoc(doc(
      db,
      'projects',
      projectId,
      'authors',
      authorId,
      'platforms',
      platformId,
      'author_posts',
      source.postId,
    ));
    if (postSnap.exists()) {
      return {
        ...(postSnap.data() as SourcePostRecord),
        id: postSnap.id,
        platformId,
      };
    }
  }

  return null;
}

function formatPostDate(value: unknown): string | null {
  if (!value) return null;
  const timestamp = getComparableDate(value);
  if (timestamp > 0) {
    return new Date(timestamp).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
  if (typeof value === 'string') return value;
  if (
    typeof value === 'object' &&
    value !== null &&
    'toDate' in value &&
    typeof (value as { toDate?: unknown }).toDate === 'function'
  ) {
    return (value as { toDate: () => Date }).toDate().toLocaleString();
  }
  return null;
}

async function loadAuthorPosts(
  projectId: string,
  authorId: string,
): Promise<SourcePostOption[]> {
  const db = CoreFirebaseService.getInstance().firestore;
  const platformsSnap = await getDocs(
    collection(db, 'projects', projectId, 'authors', authorId, 'platforms'),
  );
  const posts: SourcePostOption[] = [];

  for (const platformDoc of platformsSnap.docs) {
    const platformId = platformDoc.id;
    const postsSnap = await getDocs(
      collection(
        db,
        'projects',
        projectId,
        'authors',
        authorId,
        'platforms',
        platformId,
        'author_posts',
      ),
    );

    for (const postDoc of postsSnap.docs) {
      const data = postDoc.data() as SourcePostRecord & Record<string, any>;
      const preview = data.hook || data.body || data.title || data.sourceUrl || postDoc.id;
      posts.push({
        ...data,
        id: postDoc.id,
        platformId,
        preview,
        source: typeof data.source === 'string' ? data.source : undefined,
        engagement: data.engagement,
      });
    }
  }

  return posts.sort(
    (a, b) => getComparableDate(b.publishedAt) - getComparableDate(a.publishedAt),
  );
}

function getComparableDate(value: unknown): number {
  if (!value) return 0;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  if (typeof value !== 'object') return 0;

  const maybeTimestamp = value as {
    toMillis?: () => number;
    toDate?: () => Date;
    _seconds?: number;
    seconds?: number;
  };
  if (typeof maybeTimestamp.toMillis === 'function') {
    return maybeTimestamp.toMillis();
  }
  if (typeof maybeTimestamp.toDate === 'function') {
    return maybeTimestamp.toDate().getTime();
  }
  if (typeof maybeTimestamp._seconds === 'number') {
    return maybeTimestamp._seconds * 1000;
  }
  if (typeof maybeTimestamp.seconds === 'number') {
    return maybeTimestamp.seconds * 1000;
  }
  return 0;
}

function truncate(value: string | undefined, max: number): string {
  if (!value) return '';
  return value.length > max ? `${value.slice(0, max).trim()}…` : value;
}
